import { Injectable } from '@angular/core';
import { addBearerListToConfig } from './auth-interceptor.helper';
import { LanguageInfoDto } from './types/LanguageInfoDto';
import { MobileDeviceType } from './types/MobileDeviceType';
import { PersonDataDto } from './types/PersonDataDto';

declare const oeApp: any;
export enum ENVIRONMENT {
  STG = 'stg',
  PROD = 'prod',
}

@Injectable()
export class OeAppService {
  get environmentUrls() {
    return addBearerListToConfig({
      profileServiceUrl: this.oeProfileServiceHost,
      liveClassServiceHost: this.oeLiveClassServiceHost,
      notificationServiceHost: this.oeNotificationServiceHost,
      thirdPartyServiceHost: this.thirdPartyServiceHost,
      progressServiceHost: this.oeProgressServiceHost,
      courseServiceHost: this.courseServiceHost,
      profileImagesHost: this.oeProfileImagesHost,
      gamificationServiceHost: this.gamificationServiceHost,
      ssoServiceHost: this.ssoServiceHost,
    });
  }

  getData(key: string): any {
    return oeApp.getData(key);
  }

  getCommonData(): any {
    return oeApp.getData('common');
  }

  getPersonData(): PersonDataDto {
    return oeApp.getData('person');
  }

  hasData(key: string): boolean {
    return oeApp.hasData(key);
  }

  addData(data): void {
    oeApp.addData(data);
  }

  isMobileDevice(): boolean {
    return oeApp.isMobileDevice();
  }

  isAndroidDevice(): boolean {
    return oeApp.isAndroidDevice();
  }

  isOpenEnglishDevice(): boolean {
    return oeApp.isOpenEnglishDevice();
  }

  isIosDevice(): boolean {
    return oeApp.isIosDevice();
  }

  isIphone(): boolean {
    return oeApp.isPhone();
  }

  isIpad(): boolean {
    return oeApp.isIpad();
  }

  supportedLanguages(): LanguageInfoDto[] {
    return oeApp.supportedLanguages();
  }

  get environment(): ENVIRONMENT {
    return oeApp.isProduction() ? ENVIRONMENT.PROD : ENVIRONMENT.STG;
  }

  getMobileDeviceType() {
    if (this.isIosDevice()) {
      return MobileDeviceType.IOS;
    } else if (this.isAndroidDevice()) {
      return MobileDeviceType.ANDROID;
    }
    return MobileDeviceType.UNKNOWN;
  }

  isActivationPlacementTestEnabled() {
    const commonData = this.getCommonData();
    return commonData && commonData.isActivationPlacementTestEnabled;
  }

  accountApiGatewayHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.accountApiGatewayHost;
  }

  get gamificationServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.gamificationApiHost;
  }

  s3BucketHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.bucket;
  }

  gamificationGameRulesUrl(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.gamificationGameRulesUrl;
  }

  get courseServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.courseServiceHost;
  }

  activityPlayerHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.industryContentPlayerHost;
  }

  get thirdPartyServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeThirdpartyServiceHost;
  }

  prismicUrl(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.prismicUrl;
  }

  get webUnitIndexHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webUnitIndexHost;
  }

  get webPrivateClassesHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webPrivateClassesHost;
  }

  get webPrivateClassesReservationHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webPrivateClassesReservationHost;
  }

  get webPrivateClassesBannerHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webPrivateClassesBannerHost;
  }

  get webPlatformTourHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webPlatformTourHost;
  }

  get webProgressPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webProgressPageHost;
  }

  get webShareCertificatePageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webShareCertificatePageHost;
  }

  get webMyCoachPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webMyCoachPageHost;
  }

  get webReferralPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webReferralPageHost;
  }

  get webLcOnboardingPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webLcOnboardingPageHost;
  }

  get webLessonPlayerPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webLessonPlayerPageHost;
  }

  get webIscPlayerHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webIscPlayerHost;
  }

  get lp2LearnosityUiHost(): string {
    return this.getData('lp2LearnosityUiHost');
  }

  get contentServicesUrl(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.contentServicesUrl;
  }

  get oeProgressServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeProgressServiceHost;
  }

  get oeProfileServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeProfileServiceHost;
  }

  get webChatBotPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webChatBotPageHost;
  }

  get oeLiveClassServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeLiveClassServiceHost;
  }

  get oeNotificationServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeNotificationServiceHost;
  }

  get webNotificationsListPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webNotificationsListPageHost;
  }

  get oeProfileImagesHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeProfileImagesHost;
  }

  get webHybridClassesPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webHybridClassesPageHost;
  }

  get webInstantJoinPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webInstantJoinPageHost;
  }

  get webAccountSharingPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webAccountSharingPageHost;
  }

  get webLevelSelectionPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webLevelSelectionPageHost;
  }

  get webLiveClassScheduleSelectionPageHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webLiveClassScheduleSelectionPageHost;
  }

  get webCommonExpressionsHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webCommonExpressionsHost;
  }

  get webDailyNewsHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webDailyNewsHost;
  }

  get webInteractiveVideosHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webInteractiveVideosHost;
  }

  get oeAppDeepLink(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeAppDeepLink;
  }

  get webMobileDeepLinkUrl(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.webMobileDeepLinkUrl;
  }

  get branchIoUrl(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.branchIoUrl;
  }

  get oeLocationServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.oeLocationServiceHost;
  }

  get ssoServiceHost(): string {
    const commonData = this.getCommonData();
    return commonData && commonData.ssoServiceHost;
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/oe-app.service.ts